import { useEffect, useState } from "react";
import { ChartLine, TrendUp, Medal } from "@phosphor-icons/react";
import { AreaChart, Area, BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell, CartesianGrid } from "recharts";
import api from "@/lib/api";
import { pts } from "@/lib/format";
import { Button } from "@/components/ui/button";

const tooltipStyle = { background: "#0f1a14", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, color: "#fff" };

export default function BizReports() {
  const [d, setD] = useState(null);
  const [error, setError] = useState(false);
  const load = () => { setError(false); api.get("/business/reports").then((r) => setD(r.data)).catch(() => setError(true)); };
  useEffect(() => { load(); }, []);
  if (error && !d) return (
    <div className="pt-16 text-center space-y-3" data-testid="reports-error">
      <p className="text-muted-foreground">Couldn't load your reports.</p>
      <Button onClick={load} variant="outline" className="rounded-xl border-black/10">Retry</Button>
    </div>
  );
  if (!d) return <div className="text-muted-foreground">Loading…</div>;

  const stats = [
    { label: "Points issued", value: pts(d.points_issued), accent: true },
    { label: "Points redeemed", value: pts(d.points_redeemed) },
    { label: "Redemptions", value: d.redemptions_count ?? 0 },
    { label: "Customers", value: d.customers },
    { label: "Repeat customers", value: d.repeat_customers },
  ];
  const TIER_COLORS = ["#b08d57", "#9ca3af", "#f5b301", "#34d399", "#60a5fa"];
  const totalMembers = (d.tier_distribution || []).reduce((a, t) => a + (t.customers || 0), 0);

  return (
    <div className="space-y-6">
      <h1 className="font-display font-bold text-3xl flex items-center gap-2"><ChartLine size={26} className="text-primary" /> Reports</h1>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {stats.map((s) => (
          <div key={s.label} className="card-surface p-5" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, "-")}`}><p className={`font-display font-bold text-3xl ${s.accent ? "text-primary" : ""}`}>{s.value}</p><p className="text-xs text-muted-foreground mt-1">{s.label}</p></div>
        ))}
      </div>

      <div className="card-surface p-6" data-testid="points-trend-chart">
        <h2 className="font-display font-semibold mb-1 flex items-center gap-2"><TrendUp size={18} className="text-primary" /> Points issued vs redeemed</h2>
        <p className="text-xs text-muted-foreground mb-4">Last 14 days</p>
        <ResponsiveContainer width="100%" height={260}>
          <AreaChart data={d.daily} margin={{ left: -18, right: 8 }}>
            <defs>
              <linearGradient id="gIssued" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#34d399" stopOpacity={0.5} /><stop offset="100%" stopColor="#34d399" stopOpacity={0} /></linearGradient>
              <linearGradient id="gRedeemed" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#f59e0b" stopOpacity={0.4} /><stop offset="100%" stopColor="#f59e0b" stopOpacity={0} /></linearGradient>
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" vertical={false} />
            <XAxis dataKey="date" tick={{ fill: "#7d8a83", fontSize: 11 }} axisLine={false} tickLine={false} interval="preserveStartEnd" />
            <YAxis tick={{ fill: "#7d8a83", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
            <Tooltip contentStyle={tooltipStyle} />
            <Area type="monotone" dataKey="issued" stroke="#34d399" strokeWidth={2} fill="url(#gIssued)" name="Issued" />
            <Area type="monotone" dataKey="redeemed" stroke="#f59e0b" strokeWidth={2} fill="url(#gRedeemed)" name="Redeemed" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      <div className="card-surface p-6" data-testid="tier-growth-chart">
        <h2 className="font-display font-semibold mb-1 flex items-center gap-2"><Medal size={18} className="text-primary" /> Membership tier growth</h2>
        <p className="text-xs text-muted-foreground mb-4">{totalMembers} {totalMembers === 1 ? "member" : "members"} across your tiers</p>
        {(!d.tier_distribution || d.tier_distribution.length === 0) ? (
          <p className="text-sm text-muted-foreground">No membership tiers set up yet. Enable tiers on the Rewards Engine to track growth here.</p>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={d.tier_distribution} margin={{ left: -18, right: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.05)" vertical={false} />
                <XAxis dataKey="name" tick={{ fill: "#7d8a83", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#7d8a83", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip cursor={{ fill: "rgba(52,211,153,0.08)" }} contentStyle={tooltipStyle} />
                <Bar dataKey="customers" radius={[8, 8, 0, 0]} name="Members">{d.tier_distribution.map((_, i) => <Cell key={i} fill={TIER_COLORS[i % TIER_COLORS.length]} />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
            <div className="flex flex-wrap gap-3 mt-4">
              {d.tier_distribution.map((t, i) => (
                <div key={t.name} className="flex items-center gap-2 text-xs" data-testid={`tier-legend-${t.name.toLowerCase()}`}>
                  <span className="w-3 h-3 rounded-full" style={{ background: TIER_COLORS[i % TIER_COLORS.length] }} />
                  <span className="font-medium">{t.name}</span>
                  <span className="text-muted-foreground">· {t.customers} · ×{t.multiplier}</span>
                </div>
              ))}
            </div>
          </>
        )}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-surface p-6" data-testid="top-rewards-chart">
          <h2 className="font-display font-semibold mb-4">Top redeemed rewards</h2>
          {(!d.top_rewards || d.top_rewards.length === 0) ? <p className="text-sm text-muted-foreground">No redemptions yet.</p> : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={d.top_rewards} layout="vertical" margin={{ left: 10, right: 16 }}>
                <XAxis type="number" tick={{ fill: "#7d8a83", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <YAxis type="category" dataKey="name" width={110} tick={{ fill: "#7d8a83", fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip cursor={{ fill: "rgba(52,211,153,0.08)" }} contentStyle={tooltipStyle} />
                <Bar dataKey="count" radius={[0, 8, 8, 0]}>{d.top_rewards.map((_, i) => <Cell key={i} fill="#34d399" />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="card-surface p-6">
          <h2 className="font-display font-semibold mb-4">Staff transaction activity</h2>
          {d.staff_activity.length === 0 ? <p className="text-sm text-muted-foreground">No activity yet.</p> : (
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={d.staff_activity}>
                <XAxis dataKey="name" tick={{ fill: "#7d8a83", fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: "#7d8a83", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip cursor={{ fill: "rgba(52,211,153,0.08)" }} contentStyle={tooltipStyle} />
                <Bar dataKey="transactions" radius={[8, 8, 0, 0]}>{d.staff_activity.map((_, i) => <Cell key={i} fill="#34d399" />)}</Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
}
